import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Clock, Users, MapPin, CheckCircle, ChevronLeft, ChevronRight,
  MessageCircle, Send
} from 'lucide-react';
import { heritageWalks } from '../utils/mockData';
import { useToast } from '../hooks/use-toast';
import { sendItineraryRequest } from '../utils/emailService';

export const HeritageWalkDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  
  const [walk, setWalk] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    travelers: '2',
    message: ''
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const foundWalk = heritageWalks.find(w => w.slug === slug);
    if (foundWalk) {
      setWalk(foundWalk);
      document.title = foundWalk.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', foundWalk.metaDescription);
      }
    } else {
      navigate('/heritage-walks');
    }
  }, [slug, navigate]);

  if (!walk) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submission = {
      ...form,
      tourName: walk.name,
      tourSlug: walk.slug,
      dates: form.date,
    };
    
    try {
      // Send email via backend
      await sendItineraryRequest(submission);

      toast({
        title: "Thanks! We received your request.",
        description: "We reply within 24 hours. Meanwhile, chat with us on WhatsApp.",
        duration: 6000,
      });

      setForm({
        name: '',
        email: '',
        phone: '',
        date: '',
        travelers: '2',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Failed to send request",
        description: "Please try again or contact us via WhatsApp.",
        variant: "destructive",
        duration: 6000,
      });
      console.error('Failed to send email:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % walk.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + walk.gallery.length) % walk.gallery.length);
  };

  const getWhatsAppLink = () => {
    const walkName = language === 'en' ? walk.name : walk.nameEs;
    const message = `Hi! I'm interested in the ${walkName}. Please share more details.`;
    return `https://wa.me/917827884419?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <Header onRequestItinerary={() => {}} />

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={walk.heroImage}
          alt={`${language === 'en' ? walk.name : walk.nameEs}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
            <Link to="/heritage-walks" className="inline-flex items-center text-white/80 hover:text-white mb-4">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Heritage Walks
            </Link>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4">
              {language === 'en' ? walk.name : walk.nameEs}
            </h1>
            <p className="text-xl text-white/90 italic mb-6">
              {language === 'en' ? walk.tagline : walk.taglineEs}
            </p>
            <div className="flex flex-wrap gap-4 text-white">
              <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock className="h-5 w-5 mr-2" />
                {language === 'en' ? walk.duration : walk.durationEs}
              </div>
              <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <Users className="h-5 w-5 mr-2" />
                Max 8-10 people
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">About This Walk</h2>
              <p className="text-lg text-[#8B7D6B] leading-relaxed">
                {language === 'en' ? walk.summary : walk.summaryEs}
              </p>
            </section>

            {/* Highlights */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">Walk Highlights</h2>
              <div className="grid gap-4">
                {(language === 'en' ? walk.highlights : walk.highlightsEs).map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                    <CheckCircle className="h-5 w-5 text-[#C4B5A0] flex-shrink-0 mt-1" />
                    <span className="text-[#2C2C2C]">{highlight}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Route */}
            <section className="bg-[#F5F3F0] rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <MapPin className="h-6 w-6 text-[#C4B5A0]" />
                <h3 className="font-serif text-2xl text-[#2C2C2C]">Route Summary</h3>
              </div>
              <p className="text-[#8B7D6B]">
                {language === 'en' ? walk.route : walk.routeEs}
              </p>
            </section>

            {/* Photo Gallery */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">Gallery</h2>
              <div className="relative">
                <img
                  src={walk.gallery[currentImageIndex].url}
                  alt={walk.gallery[currentImageIndex].alt}
                  className="w-full h-[400px] object-cover rounded-lg"
                />
                
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                >
                  <ChevronLeft className="h-6 w-6 text-[#2C2C2C]" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                >
                  <ChevronRight className="h-6 w-6 text-[#2C2C2C]" />
                </button>

                <div className="flex space-x-2 mt-4 overflow-x-auto">
                  {walk.gallery.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 ${
                        index === currentImageIndex ? 'border-[#C4B5A0]' : 'border-transparent'
                      }`}
                    >
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Booking Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Quick Info */}
              <div className="bg-[#6B4E37] text-white rounded-lg p-6">
                <h3 className="font-serif text-2xl mb-4">What's Included</h3>
                <ul className="space-y-2">
                  {walk.inclusions.map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-[#C4B5A0] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Request Form */}
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="font-serif text-2xl text-[#2C2C2C] mb-6">Request Walk Details</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="border-[#D4C4B0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="border-[#D4C4B0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">WhatsApp/Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      className="border-[#D4C4B0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="date">Preferred Date</Label>
                    <Input
                      id="date"
                      name="date"
                      type="text"
                      placeholder="e.g., March 15, 2025"
                      value={form.date}
                      onChange={handleChange}
                      className="border-[#D4C4B0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="travelers">Number of People *</Label>
                    <Input
                      id="travelers"
                      name="travelers"
                      type="number"
                      min="1"
                      max="10"
                      value={form.travelers}
                      onChange={handleChange}
                      required
                      className="border-[#D4C4B0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={3}
                      value={form.message}
                      onChange={handleChange}
                      className="border-[#D4C4B0]"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#C4B5A0] hover:bg-[#6B4E37] text-white">
                    <Send className="h-4 w-4 mr-2" />
                    Request Details
                  </Button>
                </form>

                <div className="mt-4 pt-4 border-t border-[#F5F3F0]">
                  <p className="text-sm text-[#8B7D6B] text-center mb-3">Or chat with us directly</p>
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
